/**
 * Stats panel — Phase 3 overlay (DOM).
 * Shown in map scene when state.map.statsPanelOpen.
 * Also contains the Stats toggle button in .hud-map-buttons.
 * Replaces drawStatsPanel() + drawStatsButton() in canvas-render-adapter.
 */

import type { GameStateView, MageStatsView } from "../../application/ports.js";
import { t } from "../../i18n/index.js";

export interface StatsPanelCallbacks {
  onClose?: () => void;
  onToggle?: () => void;
}

export interface StatsPanel {
  update(state: GameStateView): void;
  destroy(): void;
}

export function createStatsPanel(
  container: HTMLElement,
  mapButtonsContainer: HTMLElement,
  cbs: StatsPanelCallbacks
): StatsPanel {
  // ── Stats toggle button (in map-buttons bar) ──────────────────
  const toggleBtn = document.createElement("button");
  toggleBtn.type = "button";
  toggleBtn.className = "hud-map-btn hud-map-btn--stats";
  toggleBtn.addEventListener("click", () => cbs.onToggle?.());
  mapButtonsContainer.appendChild(toggleBtn);

  // ── Stats panel modal ─────────────────────────────────────────
  const el = document.createElement("div");
  el.className = "hud-stats-panel";
  el.style.display = "none";
  container.appendChild(el);

  // Backdrop (click closes)
  const backdrop = document.createElement("div");
  backdrop.className = "hud-stats-panel__backdrop";
  backdrop.addEventListener("click", () => cbs.onClose?.());
  el.appendChild(backdrop);

  const panel = document.createElement("div");
  panel.className = "hud-stats-panel__panel hud-panel";
  el.appendChild(panel);

  // Header row
  const header = document.createElement("div");
  header.className = "hud-stats-panel__header";
  const headerTitle = document.createElement("span");
  headerTitle.className = "hud-stats-panel__header-title";
  header.appendChild(headerTitle);
  const closeBtn = document.createElement("button");
  closeBtn.type = "button";
  closeBtn.className = "hud-stats-panel__close";
  closeBtn.textContent = "\u00D7";
  closeBtn.addEventListener("click", () => cbs.onClose?.());
  header.appendChild(closeBtn);
  panel.appendChild(header);

  // Content
  const content = document.createElement("div");
  content.className = "hud-stats-panel__content";
  panel.appendChild(content);

  function renderStats(stats: MageStatsView): void {
    content.innerHTML = "";

    // ── Summary section ──────────────────────────────────────────
    const summaryHeader = document.createElement("div");
    summaryHeader.className = "hud-stats-panel__section-header";
    summaryHeader.textContent = t("stats.summaryHeader");
    content.appendChild(summaryHeader);

    const summaryRows: Array<[string, string]> = [
      [t("stats.timePlayed"),  stats.totalTimePlayedFormatted],
      [t("stats.battles"),     String(stats.totalBattles)],
      [t("stats.wins"),        String(stats.totalVictories)],
      [t("stats.losses"),      String(stats.totalFailures)],
      [t("stats.winRate"),     stats.winRate],
    ];
    for (const [label, value] of summaryRows) {
      appendStatRow(content, label, value);
    }

    // ── Slayer section ──────────────────────────────────────────
    const killsHeader = document.createElement("div");
    killsHeader.className = "hud-stats-panel__section-header";
    killsHeader.textContent = t("stats.killsHeader");
    content.appendChild(killsHeader);

    const killRows: Array<[string, string]> = [
      [t("stats.totalKills"),    String(stats.totalKills)],
      [t("stats.totalBanishes"), String(stats.totalBanishes)],
    ];
    for (const [label, value] of killRows) {
      appendStatRow(content, label, value);
    }

    const earlyHeader = document.createElement("div");
    earlyHeader.className = "hud-stats-panel__section-header";
    earlyHeader.textContent = t("stats.earlyHurryHeader");
    content.appendChild(earlyHeader);
    const earlyRows: Array<[string, string]> = [
      [t("stats.manaFromEarlyWaves"), String(stats.manaFromEarlyWaveSpawns)],
      [t("stats.earlyWaveHurries"), String(stats.earlyWaveHurries)],
    ];
    for (const [label, value] of earlyRows) {
      appendStatRow(content, label, value);
    }

    // ── Top fields section ──────────────────────────────────────
    if (stats.perField.length > 0) {
      const fieldsHeader = document.createElement("div");
      fieldsHeader.className = "hud-stats-panel__section-header";
      fieldsHeader.textContent = t("stats.fieldsHeader");
      content.appendChild(fieldsHeader);

      const table = document.createElement("table");
      table.className = "hud-stats-panel__table";

      const thead = document.createElement("thead");
      const headRow = document.createElement("tr");
      for (const col of [t("stats.colField"), t("stats.colWins"), t("stats.colFails"), t("stats.colXp")]) {
        const th = document.createElement("th");
        th.textContent = col;
        headRow.appendChild(th);
      }
      thead.appendChild(headRow);
      table.appendChild(thead);

      const tbody = document.createElement("tbody");
      for (const row of stats.perField) {
        const tr = document.createElement("tr");
        const tdField = document.createElement("td");
        tdField.textContent = row.fieldId;
        const tdWins = document.createElement("td");
        tdWins.className = "hud-stats-panel__td--wins";
        tdWins.textContent = String(row.wins);
        const tdFails = document.createElement("td");
        tdFails.className = "hud-stats-panel__td--fails";
        tdFails.textContent = String(row.fails);
        const tdXp = document.createElement("td");
        tdXp.className = "hud-stats-panel__td--xp";
        tdXp.textContent = String(row.highestXp);
        tr.appendChild(tdField);
        tr.appendChild(tdWins);
        tr.appendChild(tdFails);
        tr.appendChild(tdXp);
        tbody.appendChild(tr);
      }
      table.appendChild(tbody);
      content.appendChild(table);
    }
  }

  function appendStatRow(parent: HTMLElement, label: string, value: string): void {
    const row = document.createElement("div");
    row.className = "hud-stats-panel__stat-row";
    const lbl = document.createElement("span");
    lbl.className = "hud-stats-panel__stat-label";
    lbl.textContent = label;
    const val = document.createElement("span");
    val.className = "hud-stats-panel__stat-value";
    val.textContent = value;
    row.appendChild(lbl);
    row.appendChild(val);
    parent.appendChild(row);
  }

  function update(state: GameStateView): void {
    const isMap = state.scene === "map";
    toggleBtn.style.display = isMap ? "" : "none";
    if (isMap) {
      toggleBtn.textContent = t("stats.button");
    }

    const open = isMap && Boolean(state.map?.statsPanelOpen);
    el.style.display = open ? "" : "none";
    if (!open) return;

    headerTitle.textContent = t("stats.title");

    if (state.map?.stats) {
      renderStats(state.map.stats);
    }
  }

  function destroy(): void {
    toggleBtn.remove();
    el.remove();
  }

  return { update, destroy };
}
